/**
 * The notebook's side of the wire.
 *
 * Every call here goes through `apiJson`, which replays this browser's stored
 * credentials once if the session has lapsed underneath the open tab — the
 * notebook is the screen most likely to be opened in a tab that has been sitting
 * there since yesterday, and being thrown at a login form on the way into your
 * own archive would be the worst place in the product for that to happen.
 */

import { apiJson } from './api';

export interface NotebookFolder {
  id: string;
  name: string;
  parentId: string | null;
  /**
   * Who made it, and what it therefore refuses.
   *
   *   • `normal`  — the user's own. Renamed, moved, deleted at will.
   *   • `mode`    — one of the six the account starts with, one per button on
   *     the rail, holding everything that mode has produced. Renameable;
   *     nothing else.
   *   • `history` — the catch-all at the bottom, for work whose mode has no
   *     folder, and for whatever is left when a folder is deleted.
   */
  kind: 'history' | 'normal' | 'mode';
  /** The mode id a `mode` folder files — `translate`, `ai-prompt`, … */
  mode: string | null;
  order: number;
  createdAt: string;
}

export interface NotebookTree {
  historyId: string | null;
  folders: NotebookFolder[];
  counts: Record<string, number>;
  total: number;
}

/** What the list draws: everything but the text, plus a snippet of it. */
export interface NotebookRow {
  id: string;
  folderId: string;
  title: string;
  note: string;
  mode: string;
  sourceLang: string;
  sourceLangDetected: boolean;
  targetLang: string;
  model: string;
  ms: number;
  cached: boolean;
  auto: boolean;
  starred: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * When this exchange was last had — what the list is ordered by.
   *
   * Not `createdAt`, which is when it was first filed: History deduplicates, so
   * re-translating a sentence updates the row it already has, and ordering on
   * the creation date would leave today's work buried a month down the list.
   * See models/NotebookEntry.ts.
   */
  lastSeenAt: string;
  sourceSnippet: string;
  targetSnippet: string;
  /**
   * The one-sentence summary, when the answer carries one.
   *
   * What the row is called when its title has been cleared: somebody who empties
   * the title is asking for the generated name back, and "Untitled" names
   * nothing. See `entryJson` in the backend's lib/notebook.ts.
   */
  overview: string;
  chars: number;
}

/** What the viewer draws: the whole exchange. */
export interface NotebookEntry extends Omit<NotebookRow, 'sourceSnippet' | 'targetSnippet' | 'chars'> {
  sourceText: string;
  targetText: string;
  /** register or styleId → that tab's answer, or null for a single answer. */
  tabs: Record<string, string> | null;
  activeTab: string | null;
}

/** What Ctrl+Alt+Z sends: the panes exactly as they stand. */
export interface NotebookSnapshot {
  folderId?: string | null;
  title?: string;
  mode: string;
  sourceLang: string;
  sourceLangDetected?: boolean;
  targetLang: string;
  sourceText: string;
  targetText: string;
  tabs?: Record<string, string> | null;
  activeTab?: string | null;
  /**
   * The one-sentence summary, sent separately.
   *
   * `targetText` is the answer as the reader SEES it — the `[[OVERVIEW]]` tag
   * is stripped before it ever reaches the pane, and nobody should find it in
   * their own notebook. So the summary travels on its own and the server puts
   * it back where the model wrote it, which is what makes a hand-filed entry
   * show the same overview card as one History captured, and what NAMES the
   * entry in the list.
   */
  overview?: string | null;
  model?: string;
  ms?: number;
  cached?: boolean;
}

export function fetchNotebook(): Promise<NotebookTree> {
  return apiJson<NotebookTree>('/api/notebook');
}

export function createFolder(name: string, parentId: string | null): Promise<{ folder: NotebookFolder }> {
  return apiJson('/api/notebook/folders', {
    method: 'POST',
    body: JSON.stringify({ name, parentId }),
  });
}

export function updateFolder(
  id: string,
  patch: { name?: string; parentId?: string | null },
): Promise<{ folder: NotebookFolder }> {
  return apiJson(`/api/notebook/folders/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** `purge` deletes what was inside; without it the entries move to History. */
export function deleteFolder(id: string, purge: boolean): Promise<{ movedEntries: number; deletedEntries: number }> {
  return apiJson(`/api/notebook/folders/${id}${purge ? '?purge=1' : ''}`, { method: 'DELETE' });
}

export function fetchEntries(opts: {
  folderId?: string | null;
  q?: string;
  before?: string | null;
}): Promise<{ items: NotebookRow[]; nextBefore: string | null }> {
  const p = new URLSearchParams();
  if (opts.q) p.set('q', opts.q);
  else if (opts.folderId) p.set('folderId', opts.folderId);
  if (opts.before) p.set('before', opts.before);
  return apiJson(`/api/notebook/entries?${p.toString()}`);
}

export function fetchEntry(id: string): Promise<{ entry: NotebookEntry }> {
  return apiJson(`/api/notebook/entries/${id}`);
}

/**
 * Where a notebook view READS from.
 *
 * The dialog used to call the three functions above directly, which tied it to
 * one notebook: the signed-in account's. The admin console needs the same three
 * columns over somebody else's — the tree, the list and one entry — and the
 * honest way to give it that is to let the caller say where the rows come from,
 * not to add an "as this user" parameter to the routes above. Those resolve the
 * owner from the session and take no user id from anywhere, which is the whole
 * of the notebook's authorization story and is worth keeping in one sentence.
 *
 * Only the three READS are here. Everything that writes stays bound to the
 * signed-in account, because the only view with another source is read-only.
 */
export interface NotebookSource {
  tree(): Promise<NotebookTree>;
  entries(opts: { folderId?: string | null; q?: string; before?: string | null }): Promise<{
    items: NotebookRow[];
    nextBefore: string | null;
  }>;
  entry(id: string): Promise<{ entry: NotebookEntry }>;
}

/** This account's own notebook — what the translator opens. */
export const liveNotebook: NotebookSource = {
  tree: fetchNotebook,
  entries: fetchEntries,
  entry: fetchEntry,
};

export function saveEntry(
  snapshot: NotebookSnapshot,
): Promise<{
  entry: NotebookEntry;
  folderName: string;
  /** The folder already held this exchange; it was refreshed, not copied. */
  duplicate: boolean;
}> {
  return apiJson('/api/notebook/entries', { method: 'POST', body: JSON.stringify(snapshot) });
}

export function updateEntry(
  id: string,
  patch: { title?: string; note?: string; folderId?: string; starred?: boolean },
): Promise<{ entry: NotebookEntry }> {
  return apiJson(`/api/notebook/entries/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/**
 * The same exchange in a second folder as well.
 *
 * Moving is `updateEntry({ folderId })` — an entry belongs somewhere, and that
 * is what dragging does. This is for the case where it genuinely belongs in two
 * places, and it makes a real second row rather than a reference: see the
 * endpoint's note in routes/notebook.ts.
 */
export function copyEntry(
  id: string,
  folderId: string,
): Promise<{ entry: NotebookEntry; folderName: string; duplicate: boolean }> {
  return apiJson(`/api/notebook/entries/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify({ folderId }),
  });
}

export function deleteEntry(id: string): Promise<{ ok: true }> {
  return apiJson(`/api/notebook/entries/${id}`, { method: 'DELETE' });
}

/**
 * Where the last hand-filed entry went, remembered per browser.
 *
 * Ctrl+Alt+Z has to put the entry SOMEWHERE without asking, and "History" is
 * the wrong answer for the person who has just made a folder for what they are
 * working on: every save would land in the pile they made the folder to get out
 * of. So the chord follows the folder they last chose, and the toast says which
 * one it was — a default that is silent is only acceptable when it is also
 * visible.
 *
 * localStorage rather than the account, because it is a property of this
 * SESSION of work rather than of the person: the same user on a phone this
 * evening is doing something else.
 */
const LAST_FOLDER = 'gt.notebook.folder';

export function loadLastFolder(): string | null {
  try {
    return localStorage.getItem(LAST_FOLDER) || null;
  } catch {
    return null;
  }
}

export function saveLastFolder(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_FOLDER, id);
    else localStorage.removeItem(LAST_FOLDER);
  } catch {
    /* Storage disabled. The chord still works; it just always says History. */
  }
}
